/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  PageContainer,
  QueryContainer,
  Search,
  SelectDepartment,
  SelectViewBy,
  OutlinedButton,
  AllEmployees,
  ClockInTable,
  ClockOutTable,
  HoursWorked,
} from '../components/index';
import { useEffect, useState } from 'react';
import { apiUrl } from '../utilities/apiUrl';
import { AppBar, Tabs, Tab, Box, Grid } from '@mui/material';
import { format, subDays } from 'date-fns';
import { useOutletContext } from 'react-router-dom';
import { computeTotalHoursById, getDates } from '../utilities/totalHoursData';
import { sortByLastLogInDate } from '../utilities/sortBy';

export const Employees = () => {
  const [empId] = useOutletContext();
  const [isAdmin, setIsAdmin] = useState(false);
  const [empView, setEmpView] = useState([]);
  const [filterView, setFilterView] = useState([]);
  const [activeTimesheet, setActiveTimesheet] = useState([]);
  const [checkedOutTimesheet, setCheckedOutTimesheet] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [activeEmployeeList, setActiveEmployeeList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  //const [viewBy, setViewBy] = useState(isAdmin ? "all" : "checkedin");[EPSv5_Employees-VariableForFilteringViewBy]
  const [viewBy, setViewBy] = useState('checkedin');
  const [department, setDepartment] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [cleanData, setCleanData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartDateRange, setChartDateRange] = useState([]);
  const [cutoffData, setCutoffData] = useState([]);
  const [viewTopCharts, setViewTopCharts] = useState(false);
  const [topOfTheChartsData, setTopOfTheChartsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // GET EMPLOYEE DATA
  useEffect(() => {
    fetch(apiUrl('/employee?active=true'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        let sortedActiveEmployeeList = sortByLastLogInDate(data.data);
        setActiveEmployeeList(sortedActiveEmployeeList.reverse());

        return fetch(apiUrl('/employee'), {
          method: 'GET',
          credentials: 'include',
        });
      })
      .then((response) => response.json())
      .then((data) => {
        setEmployeeList(data.data);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department, viewBy]);

  // GET ALL DEPARTMENT DATA
  useEffect(() => {
    fetch(apiUrl('/department'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setDepartmentList(data.data);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //GET CUTOFF TIMESHEET
  useEffect(() => {
    fetch(apiUrl('/timesheet?cutoff=true'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setCutoffData(data.data);
        setTopOfTheChartsData(
          data.data.map((time) => ({
            ...time,
            ...employeeList.find((emp) => time.empid === emp._id),
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeList, department, viewBy]);

  useEffect(() => {
    fetch(apiUrl('/employee/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setIsAdmin(data.data.role !== 'REGULAR_USER' ? true : false);
        // setViewBy(data.data.role !== "REGULAR_USER" ? "all" : "checkedin");
        if (department === 'all' && searchValue === '') setFilterView(empView);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department, viewBy]);

  // GET TIMESHEET OF CURRENT DAY
  //remove startdate=${subDays(new Date(),1)}& to fix more than 24 hours bug
  //bug caused to normal hours when startdate=${subDays(new Date(),1)}& is removed
  useEffect(() => {
    fetch(
      apiUrl(
        `/timesheet?startdate=${subDays(new Date(), 1)}&enddate=${new Date()}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          const today = format(new Date(), 'MM/dd/yyyy');
          setCheckedOutTimesheet(
            data.data.filter((time) => {
              return today === format(new Date(time.checkout), 'MM/dd/yyyy');
            })
          );
          setActiveTimesheet(data.data);

          let employeeData = [];
          isAdmin
            ? (employeeData = employeeList)
            : (employeeData = activeEmployeeList);

          // Filter to get all timesheet that has null checkout
          //  note: there is a possibility that a user will login/logout multiple times per day
          const checkInTime = data.data.filter((data) => {
            return data.checkout === null;
          });

          setEmpView(
            employeeData.map((emp) => {
              const timesheetMatch = checkInTime.find((time) => time.empid === emp._id);
              const deptMatch = departmentList.find((dept) => emp.deptid === dept._id);

              return {
                ...timesheetMatch,
                ...deptMatch,
                ...emp,
              };
            })
          );

          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeList, department, viewBy]);

  const handleDepartmentChange = (e) => {
    setDepartment(e.target.value);

    if (e.target.value !== 'all') {
      setFilterView(empView.filter((data) => data.deptname === e.target.value));
    } else {
      setFilterView(empView);
    }
  };

  const handleSearch = () => {
    const filterData = empView.filter((a) => {
      return (
        a.lastname.toUpperCase() === searchValue.toUpperCase() ||
        a.firstname.toUpperCase() === searchValue.toUpperCase()
      );
    });

    setFilterView(filterData);
  };

  // COMPUTE TOTAL HOURS WORK PER EMPLOYEE
  const totalHoursWorked = (data) => {
    setChartData([]);
    data.forEach((data) => {
      let hours = data.roundedtime / 60;
      chartData.push({
        empid: data.empid,
        name: `${data.firstname} ${data.lastname}`,
        hours: hours,
      });
    });

    setChartDateRange(getDates(data));
    setCleanData(computeTotalHoursById(chartData));
  };

  // if (isLoading === false){
  return (
    <PageContainer selectedPage={2}>
      <AppBar position="static">
        <Tabs
          value={0}
          // onChange={handleChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="standard"
        >
          <Tab sx={{ minWidth: '16rem' }} label="Employees" />
        </Tabs>
      </AppBar>
      <Box sx={{ p: 3 }}>
        {/* <QueryContainer>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              [EPSv5_Employees-FilterViewBy]
              <Grid item>
                <SelectViewBy
                  viewBy={viewBy}
                  handleViewChange={(e) => {
                    setViewBy(e.target.value);
                    if (e.target.value === "all") {
                      setDepartment("all");
                    }
                  }}
                />
              </Grid>
              [EPSv5_Employees-FilterDepartment]
              <Grid item sx={{ ml: 3 }}>
                <SelectDepartment
                  departmentList={departmentList}
                  department={department}
                  handleDepartmantChange={handleDepartmentChange}
                />
              </Grid>
              <Grid item sx={{ ml: 3 }}>
                <Search
                  searchValue={searchValue}
                  onChange={(e) => setSearchValue(e.target.value.trim())}
                  handleSearch={handleSearch}
                />
              </Grid>
            </Grid>
            [EPSv5_Employees-TopCharts]
            <Grid item>
              <OutlinedButton
                onClick={() => {
                  setDepartment("");
                  setViewBy("");
                  setViewTopCharts(true);
                  totalHoursWorked(topOfTheChartsData);
                }}
                btnTitle="Top of the Charts"
                style={{
                  width: 250,
                  bgcolor: viewBy === "" ? "#84D8C5" : "none",
                  color: viewBy === "" ? "#FFF" : "#84D8C5",
                  "&:hover": {
                    color: "#FFF",
                    borderColor: "#84d8c5",
                    bgcolor:
                      viewBy === ""
                        ? "#84d8c5"
                        : "rgb(132, 216, 197, 0.2) !important",
                  },
                }}
              />
            </Grid>
          </QueryContainer> */}
        {
          /* [EPSv5_Employees-DefaultView]
          viewBy === "" && viewTopCharts ? (
            <HoursWorked
              cleanData={cleanData.sort((a, b) => a.hours - b.hours)}
              fromDate={chartDateRange[0]}
              toDate={chartDateRange[chartDateRange.length - 1]}
              title={"Top of the Charts"}
            />
          ) : viewBy === "all" && isAdmin ? (
            <AllEmployees data={filterView} view />
          ) :*/
          !isAdmin ? (
            <>
              <AllEmployees data={empView} view />
            </>
          ) : isAdmin && isLoading == false ? (
            //<></>
            <ClockInTable
              timesheetActive={activeTimesheet}
              activeEmpData={activeEmployeeList}
              departmentList={departmentList}
            /> /* [EPSv5_Employees-IsAdminAndCheckedOutFilter]
            : viewBy === "checkedout" && isAdmin ? (
              <ClockOutTable
                checkedOutTimesheet={checkedOutTimesheet}
                allEmpData={employeeList}
                pageCount={Math.floor(checkedOutTimesheet.length / 10)}
              />
            ) : viewBy === "" && viewTopCharts ? (
              <HoursWorked
                cleanData={cleanData.sort((a, b) => a.hours - b.hours)}
                fromDate={chartDateRange[0]}
                toDate={chartDateRange[chartDateRange.length - 1]}
                title={"Top of the Charts"}
              />
            ) */
          ) : (
            <AllEmployees data={filterView} view />
          )
        }
      </Box>
    </PageContainer>
  );
};

//};
